import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { PrefsSection, NOTIFICATION_ITEMS, EMAIL_ITEMS } from "@/components/settings/PrefsSection";
import { DeleteAccount } from "@/components/settings/DeleteAccount";
import { ThemeOptions } from "@/components/ThemeOptions";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, inputCls, btnPrimary, btnSecondary, cardCls, Field } from "@/components/shared";

const PasswordForm = ({ hasPassword }) => {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/password", form);
      toast.success(hasPassword ? "Password changed" : "Password set — you can now sign in with email too");
      setForm({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <form onSubmit={save} className={`${cardCls} p-6 sm:p-8 space-y-4`} data-testid="password-form">
      <h2 className="font-serif-display text-2xl font-semibold">{hasPassword ? "Change password" : "Set a password"}</h2>
      {hasPassword && <Field label="Current password"><input data-testid="current-password-input" type="password" className={inputCls} value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} required /></Field>}
      <Field label="New password"><input data-testid="new-password-input" type="password" minLength={6} className={inputCls} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} required /></Field>
      <button type="submit" data-testid="password-save-button" className={btnPrimary}>Save password</button>
    </form>
  );
};

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  if (!user || user.is_guest) return <Navigate to="/login" replace />;

  const saveName = async (e) => {
    e.preventDefault();
    try {
      setUser((await api.patch("/auth/profile", { name })).data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const signOut = async () => {
    navigate("/", { replace: true });
    await logout();
    toast("Signed out");
  };
  const methods = (user.providers || []).map((p) => (p === "google" ? "Google" : "Email & password")).join(" · ");

  return (
    <div data-testid="profile-page">
      <PageHeader eyebrow="Your account" title="Account settings" subtitle="Your profile, sign-in, notifications, emails and theme."
        action={<button data-testid="logout-button" onClick={signOut} className={btnSecondary}><LogOut className="h-4 w-4" /> Sign out</button>} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
        <form onSubmit={saveName} className={`${cardCls} p-6 sm:p-8 space-y-4`} data-testid="profile-form">
          <div className="flex items-center gap-4 mb-2">
            <div className="h-14 w-14 rounded-2xl bg-t-primary text-white flex items-center justify-center font-serif-display text-2xl font-semibold">{(user.name || "?")[0].toUpperCase()}</div>
            <div>
              <p className="font-serif-display text-2xl font-semibold" data-testid="profile-name">{user.name}</p>
              <p className="text-sm text-t-muted" data-testid="profile-email">{user.email}</p>
            </div>
          </div>
          <Field label="Name"><input data-testid="profile-name-input" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required /></Field>
          <Field label="Email"><input className={`${inputCls} opacity-70`} value={user.email} readOnly data-testid="profile-email-input" /></Field>
          <p className="text-xs text-t-muted" data-testid="profile-methods">Signs in with: {methods}</p>
          <button type="submit" data-testid="profile-save-button" className={btnPrimary}>Save changes</button>
        </form>
        <PasswordForm hasPassword={user.has_password} />
        <PrefsSection testId="notifications-section" title="Notifications" subtitle="Choose what the app shows you." items={NOTIFICATION_ITEMS} />
        <PrefsSection testId="email-prefs-section" title="Email preferences" subtitle="Password-reset and security emails are always sent." items={EMAIL_ITEMS} />
        <section className={`${cardCls} p-6 sm:p-8 lg:col-span-2`} data-testid="theme-section">
          <h2 className="font-serif-display text-2xl font-semibold mb-4">Theme</h2>
          <ThemeOptions grid />
        </section>
        <div className="lg:col-span-2"><DeleteAccount /></div>
      </div>
    </div>
  );
}
